import React, { useRef, useState, useEffect, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Stage, Layer, Group, Rect, Circle, Text, Path } from "react-konva";
import { v4 as uuidv4 } from "uuid";
import {
  getTablesByRestaurant,
  updateTable,
  createTable,
  deleteTable,
} from "../../api/owner/restaurants/tables";
import { getAllRestaurantsWithoutPagination } from "../../api/owner/restaurants";
import useImage from "use-image";
import { Image as KonvaImage } from "react-konva";
import { useLocation } from "react-router-dom";
import { useTranslation } from "../../hooks/useTranslation";

const PopupModal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed sm:inset-0 bottom-0 left-0 right-0 bg-black bg-opacity-50 flex sm:items-center items-end justify-center z-50 h-full">
      <div className="bg-white sm:rounded-[20px] rounded-t-[20px] w-full sm:w-[845px] relative sm:shadow-lg">
        <button
          onClick={onClose}
          className="absolute top-[33px] right-[30px] text-[#9E9E9E] hover:text-black"
        >
          ✕
        </button>
        {children}
      </div>
    </div>
  );
};

const useDebouncedCallback = (callback, delay) => {
  const timer = useRef();
  return useCallback(
    (...args) => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        callback(...args);
      }, delay);
    },
    [callback, delay]
  );
};

const FloorDesigner = () => {
  const { t } = useTranslation();
  const translations = t("floorPlan");
  const stageRef = useRef();
  const queryClient = useQueryClient();
  const [selectedRestaurant, setSelectedRestaurant] = useState("");
  const [tables, setTables] = useState([]);
  const [nextTableNumber, setNextTableNumber] = useState(1);
  const [chairCount, setChairCount] = useState(4);
  const [tableType, setTableType] = useState("round");
  const [scale, setScale] = useState(0.6);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [editingTable, setEditingTable] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false)
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 1366, height: 700 });
  const location = useLocation();
  const { restaurantId } = location.state || {};


  const closeModel = () => {
    setIsPopupOpen(false)
    setEditingTable(null)
  }

  const MAX_TABLES = 40;

  const tableConfigs = {
    round: {
      baseRadius: 60,
      topRadius: 70,
      height: 20,
      chairDistance: 100,
      chairAngle: 360
    },
    square: {
      width: 140,
      depth: 140,
      height: 20,
      chairDistance: 110,
      chairAngle: 360
    },
    rectangle: {
      width: 180,
      depth: 100,
      height: 20,
      chairDistance: 110,
      chairAngle: 360
    }
  };

  const { data: restaurantList } = useQuery({
    queryKey: ["restaurants"],
    queryFn: getAllRestaurantsWithoutPagination,
  });

  const {
    data: tableData,
    isLoading: loadingTables,
  } = useQuery({
    queryKey: ["tables", restaurantId],
    queryFn: () => getTablesByRestaurant(restaurantId),
    enabled: !!restaurantId,
  });

  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        setDimensions({
          width: containerRef.current.offsetWidth,
          height: containerRef.current.offsetHeight,
        });
      }
    };
    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);


  useEffect(() => {
    if (tableData?.data?.tables?.length) {
      const transformed = tableData.data.tables.map((table) => {
        const config = tableConfigs[table.type] || {};
        return {
          ...table,
          id: table._id,
          name: table.tableNumber,
          ...config
        };
      });

      setTables(transformed);

      const maxNumber = Math.max(
        ...transformed.map((table) => parseInt(table.name || "0"))
      );
      setNextTableNumber(maxNumber + 1);
    }
  }, [tableData]);


  const updateTableMutation = useMutation({
    mutationFn: updateTable,
    onSuccess: () => {
      queryClient.invalidateQueries(["tables", restaurantId]);
    },
  });

  const createTableMutation = useMutation({
    mutationFn: (newTableData) => createTable(newTableData),
    onSuccess: () => {
      queryClient.invalidateQueries(["tables", restaurantId]);
    },
  });

  const debouncedUpdate = useDebouncedCallback((updatedTable) => {
    updateTableMutation.mutate(updatedTable);
  }, 3000);

  const handleDrag = (e, id) => {
    const updated = tables.map((table) =>
      table.id === id ? { ...table, x: e.target.x(), y: e.target.y() } : table
    );
    setTables(updated);
    const changedTable = updated.find((t) => t.id === id);
    debouncedUpdate(changedTable);
  };

  const rotateTable = (id) => {
    const updated = tables.map((table) =>
      table.id === id
        ? { ...table, rotation: (table.rotation + 45) % 360 }
        : table
    );
    setTables(updated);
    const changedTable = updated.find((t) => t.id === id);
    debouncedUpdate(changedTable);
  };

  const addTable = () => {
    if (!restaurantId) {
      alert(translations?.alerts?.selectRestaurant || "Please select a restaurant.");
      return;
    }

    if (tables.length >= MAX_TABLES) {
      alert((translations?.alerts?.maxTables || "Maximum {{max}} tables allowed").replace("{{max}}", MAX_TABLES));
      return;
    }

    let config = tableConfigs[tableType];

    if (tableType === "rectangle") {
      const chairs = Math.min(Math.max(parseInt(chairCount) || 1, 1), 20);
      const perSide = Math.ceil(chairs / 4);
      const minSpacing = 50;

      config = {
        ...config,
        width: Math.max(180, perSide * minSpacing),
        depth: Math.max(100, perSide * minSpacing * 0.8)
      };
    }

    const newTable = {
      id: uuidv4(),
      x: 200 + tables.length * 50,
      y: 200,
      type: tableType,
      ...config,
      name: nextTableNumber.toString(), // used in frontend only
      tableNumber: nextTableNumber.toString(), // used for API
      restaurantId: restaurantId,
      status: "available",
      chairs: Math.min(Math.max(parseInt(chairCount) || 1, 1), 20),
      rotation: 0
    };

    setTables([...tables, newTable]);
    setNextTableNumber(nextTableNumber + 1);

    // Send to backend
    createTableMutation.mutate({
      restaurantId: newTable.restaurantId,
      tableNumber: newTable.tableNumber,
      type: newTable.type,
      chairs: newTable.chairs,
      x: newTable.x,
      y: newTable.y,
      rotation: newTable.rotation
    });
  };


  const handleWheel = (e) => {
    e.evt.preventDefault();
    const scaleBy = 1.05;
    const stage = stageRef.current;
    const oldScale = stage.scaleX();
    const pointer = stage.getPointerPosition();

    const mousePointTo = {
      x: (pointer.x - stage.x()) / oldScale,
      y: (pointer.y - stage.y()) / oldScale,
    };

    const newScale = e.evt.deltaY > 0 ? oldScale / scaleBy : oldScale * scaleBy;
    setScale(Math.min(Math.max(newScale, 0.5), 3));
    setPosition({
      x: pointer.x - mousePointTo.x * newScale,
      y: pointer.y - mousePointTo.y * newScale,
    });
  };

  const renderTable = (table) => {
    const fillColor = table.status === "reserved" ? "#E53E3E" : "#8B4513";
    const shadowColor = table.status === "reserved" ? "#822727" : "#5D2906";

    if (table.type === "round") {
      return (
        <Group>
          <Circle
            x={0}
            y={10}
            radius={table.baseRadius}
            fill={shadowColor}
            opacity={0.7}
          />
          {Array.from({ length: 8 }).map((_, i) => {
            const angle = (i * 45) * (Math.PI / 180);
            return (
              <Path
                key={`grain-${i}`}
                x={0}
                y={0}
                data={`M0,0 L${Math.cos(angle) * table.topRadius},${Math.sin(angle) * table.topRadius}`}
                stroke="#A0522D"
                strokeWidth={1}
              />
            );
          })}
          <Circle
            x={0}
            y={0}
            radius={table.topRadius}
            fill={fillColor}
            stroke="#5D2906"
            strokeWidth={2}
          />
          <Circle
            x={0}
            y={0}
            radius={table.topRadius}
            fill="transparent"
            stroke="#5D2906"
            strokeWidth={2}
          />
        </Group>
      );
    } else {
      return (
        <Group>
          <Rect
            x={-table.width / 2}
            y={-table.depth / 2 + 10}
            width={table.width}
            height={table.depth}
            cornerRadius={5}
            fill={shadowColor}
            opacity={0.7}
          />
          {Array.from({ length: 5 }).map((_, i) => (
            <Rect
              key={`grain-${i}`}
              x={-table.width / 2 + 5}
              y={-table.depth / 2 + i * (table.depth / 5)}
              width={table.width - 10}
              height={2}
              fill="#A0522D"
              opacity={0.3}
            />
          ))}
          <Rect
            x={-table.width / 2}
            y={-table.depth / 2}
            width={table.width}
            height={table.depth}
            cornerRadius={5}
            fill={fillColor}
            stroke="#5D2906"
            strokeWidth={2}
          />
          <Rect
            x={-table.width / 2}
            y={-table.depth / 2}
            width={table.width}
            height={table.depth}
            cornerRadius={5}
            fill="transparent"
            stroke="#5D2906"
            strokeWidth={2}
          />
        </Group>
      );
    }
  };

  const renderTableText = (table) => {
    const isRound = table.type === "round";
    const centerX = 0;
    const centerY = 0;
    const fontSize = 32;

    return (
      <Group>
        <Text
          text={`${table.name}`}
          fontSize={fontSize}
          fontStyle="bold"
          fill="white"
          align="center"
          verticalAlign="middle"
          width={isRound ? table.topRadius * 2 : table.width}
          height={fontSize + 10}
          x={isRound ? -table.topRadius : -table.width / 2}
          y={-fontSize / 2}
        />

        {table.status === "reserved" && (
          <Text
            text={translations?.reserved || "RESERVED"}
            fontSize={18}
            fontStyle="bold"
            fill="white"
            align="center"
            width={isRound ? table.topRadius * 2 : table.width}
            height={20}
            x={isRound ? -table.topRadius : -table.width / 2}
            y={fontSize / 2}
          />
        )}
      </Group>
    );
  };

  const renderChairs = (table) => {
    const chairs = [];
    const chairWidth = 40;
    const chairDepth = 40;
    const chairColor = "#8B4513";
    const chairShadow = "#5D2906";
    const chairBackColor = "#A0522D";

    if (table.type === "round") {
      for (let i = 0; i < table.chairs; i++) {
        const angle = (i * (table.chairAngle / table.chairs) + table.rotation) * (Math.PI / 180);
        const x = Math.cos(angle) * table.chairDistance;
        const y = Math.sin(angle) * table.chairDistance;

        chairs.push(
          <Group
            key={`chair-${i}`}
            x={x}
            y={y}
            rotation={(angle * 180) / Math.PI + 90}
          >

            <Rect x={-chairWidth / 2} y={-chairDepth / 2 + 5} width={chairWidth} height={chairDepth} cornerRadius={5} fill={chairShadow} opacity={0.6} />
            <Rect x={-chairWidth / 2 + 5} y={chairDepth / 2 - 10} width={6} height={15} fill="#5D2906" cornerRadius={2} />
            <Rect x={chairWidth / 2 - 11} y={chairDepth / 2 - 10} width={6} height={15} fill="#5D2906" cornerRadius={2} />
            <Rect x={-chairWidth / 2} y={-chairDepth / 2 + 15} width={chairWidth} height={10} fill={chairColor} stroke="#5D2906" strokeWidth={1} cornerRadius={3} />
            <Rect x={-chairWidth / 2} y={-chairDepth / 2} width={chairWidth} height={15} fill={chairBackColor} stroke="#5D2906" strokeWidth={1} cornerRadius={3} />
            {Array.from({ length: 3 }).map((_, j) => (
              <Rect key={`slat-${j}`} x={-chairWidth / 2 + 5 + j * (chairWidth / 3)} y={-chairDepth / 2 + 2} width={4} height={11} fill="#8B4513" cornerRadius={1} />
            ))}
            <Rect x={-chairWidth / 2 - 5} y={-chairDepth / 2 + 10} width={5} height={20} fill={chairColor} cornerRadius={2} />
            <Rect x={chairWidth / 2} y={-chairDepth / 2 + 10} width={5} height={20} fill={chairColor} cornerRadius={2} />
          </Group>
        );
      }
    } else {
      const perSide = Math.ceil(table.chairs / 4);
      const chairSpacing = 10;
      const chairWidth = 40;
      const chairDepth = 40;

      const availableWidth = table.width - chairWidth - 2 * chairSpacing;
      const availableDepth = table.depth - chairDepth - 2 * chairSpacing;

      const spacingX = perSide > 1 ? availableWidth / (perSide - 1) : 0;
      const spacingY = perSide > 1 ? availableDepth / (perSide - 1) : 0;


      // Top side
      for (let i = 0; i < perSide && chairs.length < table.chairs; i++) {
        chairs.push(
          <Group
            key={`chair-top-${i}`}
            x={-availableWidth / 2 + spacingX * i + chairSpacing}
            y={-table.depth / 2 - 30}
            rotation={0}
          >
            {renderSingleChair()}
          </Group>
        );
      }

      // Right side
      for (let i = 0; i < perSide && chairs.length < table.chairs; i++) {
        chairs.push(
          <Group
            key={`chair-right-${i}`}
            x={table.width / 2 + 30}
            y={-availableDepth / 2 + spacingY * i + chairSpacing}
            rotation={90}
          >
            {renderSingleChair()}
          </Group>
        );
      }

      // Bottom side
      for (let i = 0; i < perSide && chairs.length < table.chairs; i++) {
        chairs.push(
          <Group
            key={`chair-bottom-${i}`}
            x={-availableWidth / 2 + spacingX * i + chairSpacing}
            y={table.depth / 2 + 30}
            rotation={180}
          >
            {renderSingleChair()}
          </Group>
        );
      }

      // Left side
      for (let i = 0; i < perSide && chairs.length < table.chairs; i++) {
        chairs.push(
          <Group
            key={`chair-left-${i}`}
            x={-table.width / 2 - 30}
            y={-availableDepth / 2 + spacingY * i + chairSpacing}
            rotation={-90}
          >
            {renderSingleChair()}
          </Group>
        );
      }
    }


    return chairs;

    // 🪑 Reusable chair rendering (rectangle chair with details)
    function renderSingleChair() {
      return (
        <>
          <Rect x={-chairWidth / 2} y={-chairDepth / 2 + 5} width={chairWidth} height={chairDepth} cornerRadius={5} fill={chairShadow} opacity={0.6} />
          <Rect x={-chairWidth / 2 + 5} y={chairDepth / 2 - 10} width={6} height={15} fill="#5D2906" cornerRadius={2} />
          <Rect x={chairWidth / 2 - 11} y={chairDepth / 2 - 10} width={6} height={15} fill="#5D2906" cornerRadius={2} />
          <Rect x={-chairWidth / 2} y={-chairDepth / 2 + 15} width={chairWidth} height={10} fill={chairColor} stroke="#5D2906" strokeWidth={1} cornerRadius={3} />
          <Rect x={-chairWidth / 2} y={-chairDepth / 2} width={chairWidth} height={15} fill={chairBackColor} stroke="#5D2906" strokeWidth={1} cornerRadius={3} />
          {Array.from({ length: 3 }).map((_, j) => (
            <Rect key={`slat-${j}`} x={-chairWidth / 2 + 5 + j * (chairWidth / 3)} y={-chairDepth / 2 + 2} width={4} height={11} fill="#8B4513" cornerRadius={1} />
          ))}
          <Rect x={-chairWidth / 2 - 5} y={-chairDepth / 2 + 10} width={5} height={20} fill={chairColor} cornerRadius={2} />
          <Rect x={chairWidth / 2} y={-chairDepth / 2 + 10} width={5} height={20} fill={chairColor} cornerRadius={2} />
        </>
      );
    }
  };

  const [backgroundImage] = useImage("https://images.unsplash.com/photo-1595871213029-7476de0d71dd?q=80&w=1069&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D");



  return (
    <div className="p-4">
      <div className="mb-4 flex flex-wrap gap-4">

        <input
          type="number"
          min="1"
          max="20"
          value={chairCount}
          onChange={(e) => setChairCount(e.target.value)}
          className="border rounded px-3 py-2 w-20"
        />

        <select
          value={tableType}
          onChange={(e) => setTableType(e.target.value)}
          className="border rounded px-3 py-2"
        >
          <option value="round">{translations?.tableTypes?.round || "Round"}</option>
          <option value="square">{translations?.tableTypes?.square || "Square"}</option>
          <option value="rectangle">{translations?.tableTypes?.rectangle || "Rectangle"}</option>
        </select>

        <button
          onClick={addTable}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          {translations?.addTable || "Add Table #"}{nextTableNumber}
        </button>
      </div>

      <div
  ref={containerRef}
  className="border border-gray-300 rounded-lg overflow-hidden w-full bg-gray-50"
  style={{ height: "500px" }} // ✅ Reduced canvas height
>
  <Stage
    width={dimensions.width}
    height={500} // ✅ Make sure Stage height matches container
    scaleX={scale}
    scaleY={scale}
    x={position.x}
    y={position.y}
    draggable
    ref={stageRef}
    onWheel={handleWheel}
  >
    {/* ✅ Layer 1: Static background image, not scaled or moved */}
    <Layer listening={false}>
      {backgroundImage && (
        <KonvaImage
          image={backgroundImage}
          x={0}
          y={0}
          width={3000}
          height={1400}
          listening={false}
        />
      )}
    </Layer>

    {/* ✅ Layer 2: Zoomable + pannable content (tables + grid) */}
    <Layer>
      {/* Grid */}
      {Array.from({ length: Math.ceil(window.innerWidth / 50) }).map((_, i) => (
        <React.Fragment key={`grid-${i}`}>
          <Rect x={i * 50} y={0} width={1} height={1400} fill="#e5e7eb" />
          <Rect x={0} y={i * 50} width={3000} height={1} fill="#e5e7eb" />
        </React.Fragment>
      ))}

      {/* Tables */}
      {tables?.map((table) => (
        <Group
          key={table.id}
          x={table.x}
          y={table.y}
          draggable
          rotation={table.rotation}
          onDragEnd={(e) => handleDrag(e, table.id)}
          onDblClick={() => rotateTable(table.id)}
          onClick={() => {
            setEditingTable(table);
            setIsPopupOpen(true);
          }}
        >
          {renderTable(table)}
          {renderChairs(table)}
          {renderTableText(table)}
        </Group>
      ))}
    </Layer>
  </Stage>
</div>


      {/* <div
        ref={containerRef}
        className="border border-gray-300 rounded-lg overflow-hidden w-full h-[700px] bg-gray-50"
      >
        <Stage
          width={dimensions.width}
          height={700}
          draggable
          scaleX={scale}
          scaleY={scale}
          x={position.x}
          y={position.y}
          ref={stageRef}
          onWheel={handleWheel}
        >
          <Layer>
            {backgroundImage && (
              <KonvaImage
                image={backgroundImage}
                x={0}
                y={0}
                width={3000}
               height={1400}
                listening={false}
              />
            )}
            {Array.from({ length: Math.ceil(window.innerWidth / 50) }).map((_, i) => (
              <React.Fragment key={`grid-${i}`}>
                <Rect
                  x={i * 50}
                  y={0}
                  width={1}
                  height={700}
                  fill="#e5e7eb"
                />
                <Rect
                  x={0}
                  y={i * 50}
                  width={window.innerWidth}
                  height={1}
                  fill="#e5e7eb"
                />
              </React.Fragment>
            ))}

            {tables?.map((table) => (
              <Group
                key={table.id}
                x={table.x}
                y={table.y}
                draggable
                rotation={table.rotation}
                onDragEnd={(e) => handleDrag(e, table.id)}
                onDblClick={() => rotateTable(table.id)}
                // onClick={() => toggleStatus(table.id)}
                onClick={() => {
                  setEditingTable(table)
                  setIsPopupOpen(true)
                }}

              >
                {renderTable(table)}
                {renderChairs(table)}
                {renderTableText(table)}
              </Group>
            ))}
          </Layer>
        </Stage>
      </div> */}



      <PopupModal isOpen={isPopupOpen} onClose={closeModel}>
        <div className="w-full rounded-[20px] sm:p-[30px] p-[15px]">
          <h2 className="text-[22px] font-medium mb-[19px]">{translations?.editTable || "Edit Table #"}{editingTable?.name}</h2>

          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium">{translations?.tableNumber || "Table Number"}</label>
              <input
                type="text"
                className="w-full border px-3 py-2 rounded"
                value={editingTable?.name}
                onChange={(e) =>
                  setEditingTable({ ...editingTable, name: e.target.value })
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium">{translations?.chairs || "Chairs"}</label>
              <input
                type="text"
                className="w-full border px-3 py-2 rounded"
                value={editingTable?.chairs}
                onChange={(e) =>
                  setEditingTable({ ...editingTable, chairs: e.target.value })
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium">{translations?.tableType || "Table Type"}</label>
              <select
                value={editingTable?.type}
                onChange={(e) =>
                  setEditingTable({
                    ...editingTable,
                    type: e.target.value,
                    ...tableConfigs[e.target.value],
                  })
                }
                className="w-full border px-3 py-2 rounded"
              >
                <option value="round">{translations?.tableTypes?.round || "Round"}</option>
                <option value="square">{translations?.tableTypes?.square || "Square"}</option>
                <option value="rectangle">{translations?.tableTypes?.rectangle || "Rectangle"}</option>
              </select>
            </div>

          </div>


          <div className="flex gap-[30px] justify-end pt-[23px] border-t border-[#EEEEEE]">
            <button
              onClick={() => setIsPopupOpen(false)}
              className="text-white font-bold py-[14px] px-[31px] rounded-[2px] bg-[#CCCCCC] hover:bg-[#B8B8B8] text-[20px] text-center gray-shadow"
            >
              {translations?.cancel || "Cancel"}
            </button>
            <button
              onClick={() => {
                const updated = {
                  ...editingTable,
                  tableNumber: editingTable.name,
                };
                setTables((prev) =>
                  prev.map((t) => (t.id === updated.id ? updated : t))
                );
                updateTableMutation.mutate({
                  id: updated.id,
                  restaurantId: restaurantId,
                  tableNumber: updated.tableNumber,
                  type: updated.type,
                  x: updated.x,
                  y: updated.y,
                  rotation: updated.rotation,
                  chairs: updated.chairs,
                  status: updated.status,
                });
                setEditingTable(null);
                setIsPopupOpen(false)
              }}
              className="rounded-[2px] bg-[#4CAF50] text-base text-white font-bold py-[14px] px-[30px] green-shadow hover:bg-[#209b25]"
            >
              {translations?.update || "Update"}
            </button>
          </div>
        </div>
      </PopupModal>


      <div className="mt-4 text-sm text-gray-600">
        <p><strong>{translations?.tips?.title || "Tips:"}</strong></p>
        <ul className="list-disc pl-5 space-y-1">
          <li>{translations?.tips?.clickTable || "Click on a table to toggle reservation status"}</li>
          <li>{translations?.tips?.doubleClick || "Double-click to rotate the table"}</li>
          <li>{translations?.tips?.scrollZoom || "Scroll to zoom, drag to pan"}</li>
          <li>{translations?.tips?.shiftDrag || "Hold Shift while dragging for precise movement"}</li>
        </ul>
      </div>


    </div>
  );
};

export default FloorDesigner;

